/**
 * GMDashboardPage.js — General Manager dashboard
 * Live KPIs (GET /api/kpi/live/) + daily trend from snapshots (GET /api/kpi/snapshots/).
 * "Save today's snapshot" calls POST /api/kpi/refresh/ so the trend builds up over time.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { PageHeader, Loading, fmtKES, btnSm } from '../components/UI';
import { KpiCard, DashCard, AreaChart, BarChartV, DonutRing, Gauge, fmtShort } from '../components/DashboardWidgets';
import * as api from '../api/client';

const gold   = '#C9A84C';
const green  = '#2ecc71';
const red    = '#e74c3c';
const orange = '#f39c12';
const blue   = '#3498db';
const purple = '#9b59b6';

const num = (v) => Number(v) || 0;
const grid = (min) => ({ display: 'grid', gridTemplateColumns: `repeat(auto-fit,minmax(${min}px,1fr))`, gap: 14, marginBottom: 14 });

const RANGES = [7, 30, 90];

export default function GMDashboardPage({ notify }) {
  const [live, setLive]           = useState(null);
  const [liveErr, setLiveErr]     = useState('');
  const [snaps, setSnaps]         = useState([]);
  const [days, setDays]           = useState(30);
  const [saving, setSaving]       = useState(false);
  const [updatedAt, setUpdatedAt] = useState(null);

  const loadLive = useCallback(() => {
    api.getKPILive()
      .then(r => { setLive(r.data); setLiveErr(''); setUpdatedAt(new Date()); })
      .catch(err => setLiveErr(err?.response?.data?.error || err?.message || 'Failed to load live KPIs'));
  }, []);

  const loadSnaps = useCallback(() => {
    api.getKPISnapshots(`?days=${days}`)
      .then(r => {
        const rows = r.data?.results || r.data || [];
        setSnaps([...rows].sort((a, b) => (a.date < b.date ? -1 : 1)));
      })
      .catch(() => setSnaps([]));
  }, [days]);

  useEffect(() => {
    loadLive();
    const t = setInterval(loadLive, 60000);   // live tiles refresh every minute
    return () => clearInterval(t);
  }, [loadLive]);

  useEffect(() => { loadSnaps(); }, [loadSnaps]);

  const saveSnapshot = async () => {
    setSaving(true);
    try {
      await api.refreshKPI(new Date().toISOString().slice(0, 10));
      notify?.("Today's KPI snapshot saved ✓");
      loadSnaps();
      loadLive();
    } catch (err) {
      notify?.(err?.response?.data?.error || err?.message || 'Failed to save snapshot', 'error');
    } finally { setSaving(false); }
  };

  if (!live && !liveErr) return <Loading />;

  const header = (
    <PageHeader
      icon="📊"
      title="GM Dashboard"
      sub={updatedAt ? `Live hotel performance · updated ${updatedAt.toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit' })}` : 'Live hotel performance'}
      action={
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button style={btnSm} onClick={loadLive}>↻ Refresh</button>
          <button style={{ ...btnSm, background: gold, color: '#fff', borderColor: gold }} onClick={saveSnapshot} disabled={saving}>
            {saving ? 'Saving…' : "💾 Save today's snapshot"}
          </button>
        </div>
      }
    />
  );

  if (liveErr && !live) {
    return (
      <div>
        {header}
        <DashCard>
          <div style={{ padding: 30, textAlign: 'center', color: red, fontSize: 14 }}>
            ⚠ {liveErr}
            <div style={{ marginTop: 12 }}><button style={btnSm} onClick={loadLive}>Try again</button></div>
          </div>
        </DashCard>
      </div>
    );
  }

  const rooms   = live.rooms || {};
  const rev     = live.revenue || {};
  const kpis    = live.kpis || {};
  const mtd     = live.mtd || {};
  const guests  = live.guests || {};
  const maint   = live.maintenance || {};
  const crm     = live.crm || {};

  const occ        = num(rooms.occupancy_rate);
  const occColor   = occ >= 75 ? green : occ >= 45 ? gold : orange;
  const profit     = num(mtd.profit);
  const margin     = num(mtd.revenue) > 0 ? Math.round((profit / num(mtd.revenue)) * 100) : 0;

  const revSpark   = snaps.map(s => num(s.total_revenue));
  const occSpark   = snaps.map(s => num(s.occupancy_rate));
  const adrSpark   = snaps.map(s => num(s.adr));
  const revparSpark= snaps.map(s => num(s.revpar));
  const shortDate  = (d) => { const x = new Date(d); return `${x.getDate()}/${x.getMonth() + 1}`; };

  const revTrend   = snaps.map(s => ({ label: shortDate(s.date), value: num(s.total_revenue) }));
  const occTrend   = snaps.map(s => ({ label: shortDate(s.date), value: num(s.occupancy_rate) }));

  const mixSegs = [
    { label: 'Rooms',      value: num(rev.room),       color: gold },
    { label: 'F&B',        value: num(rev.fnb),        color: blue },
    { label: 'Conference', value: num(rev.conference), color: purple },
  ].filter(s => s.value > 0);

  const roomSegs = [
    { label: `Occupied ${num(rooms.occupied)}`,       value: num(rooms.occupied),    color: gold },
    { label: `Available ${num(rooms.available)}`,     value: num(rooms.available),   color: green },
    { label: `Maintenance ${num(rooms.maintenance)}`, value: num(rooms.maintenance), color: red },
  ].filter(s => s.value > 0);

  const alerts = [
    { label: 'Critical work orders', value: num(maint.critical),        color: red,    icon: '🚨' },
    { label: 'Open work orders',     value: num(maint.open_work_orders),color: orange, icon: '🔧' },
    { label: 'Overdue PM tasks',     value: num(maint.pm_overdue),      color: orange, icon: '🗓️' },
    { label: 'Open complaints',      value: num(crm.open_complaints),   color: purple, icon: '💬' },
  ];

  const rangeBtns = (
    <div style={{ display: 'flex', gap: 4 }}>
      {RANGES.map(r => (
        <button key={r} onClick={() => setDays(r)}
          style={{ ...btnSm, padding: '3px 9px', fontSize: 11,
            background: days === r ? gold : 'transparent', color: days === r ? '#fff' : 'var(--textMuted)',
            borderColor: days === r ? gold : 'var(--border)' }}>
          {r}d
        </button>
      ))}
    </div>
  );

  return (
    <div>
      {header}

      {/* ── Today's headline KPIs ───────────────────────────────────────── */}
      <div style={grid(170)}>
        <KpiCard label="Revenue today" value={fmtKES(rev.total)} icon="💰" color={gold}  spark={revSpark}
          sub={`Rooms ${fmtShort(num(rev.room))} · F&B ${fmtShort(num(rev.fnb))} · Conf ${fmtShort(num(rev.conference))}`} />
        <KpiCard label="Occupancy" value={`${occ}%`} icon="🛏️" color={occColor} spark={occSpark}
          sub={`${num(rooms.occupied)} of ${num(rooms.total) - num(rooms.maintenance)} sellable rooms`} />
        <KpiCard label="ADR" value={fmtKES(kpis.adr)} icon="🏷️" color={blue} spark={adrSpark}
          sub="Average daily rate" />
        <KpiCard label="RevPAR" value={fmtKES(kpis.revpar)} icon="📈" color={purple} spark={revparSpark}
          sub="Revenue per available room" />
      </div>

      {/* ── Month to date + guests ──────────────────────────────────────── */}
      <div style={grid(170)}>
        <KpiCard label="MTD revenue"  value={fmtKES(mtd.revenue)}  icon="📅" color={green} />
        <KpiCard label="MTD expenses" value={fmtKES(mtd.expenses)} icon="🧾" color={orange} />
        <KpiCard label="MTD profit"   value={fmtKES(profit)} icon={profit >= 0 ? '✅' : '⚠️'}
          color={profit >= 0 ? green : red} sub={`${margin}% margin`} />
        <KpiCard label="Guests today" value={`${num(guests.checkins)} in · ${num(guests.checkouts)} out`} icon="🧳" color={blue}
          sub="Check-ins · check-outs" />
      </div>

      {/* ── Trends ──────────────────────────────────────────────────────── */}
      <div style={grid(320)}>
        <DashCard title="Revenue trend" action={rangeBtns}>
          {revTrend.length
            ? <AreaChart data={revTrend} color={gold} height={170} formatY={v => 'K' + Math.round(v / 1000)} />
            : <EmptyTrend onSave={saveSnapshot} saving={saving} />}
        </DashCard>
        <DashCard title="Occupancy trend (%)">
          {occTrend.length
            ? <BarChartV data={occTrend} color={blue} height={170} formatY={v => Math.round(v) + '%'} />
            : <EmptyTrend onSave={saveSnapshot} saving={saving} />}
        </DashCard>
      </div>

      {/* ── Mix, rooms, alerts ──────────────────────────────────────────── */}
      <div style={grid(240)}>
        <DashCard title="Occupancy now" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Gauge value={occ} max={100} color={occColor} label="rooms occupied" size={150} />
        </DashCard>

        <DashCard title="Revenue mix today">
          {mixSegs.length
            ? <DonutRing segments={mixSegs} size={130} stroke={14} centerLabel={fmtShort(num(rev.total))} centerSub="KES" />
            : <Muted>No revenue recorded yet today</Muted>}
        </DashCard>

        <DashCard title="Room status">
          {roomSegs.length
            ? <DonutRing segments={roomSegs} size={130} stroke={14} centerLabel={num(rooms.total)} centerSub="rooms" />
            : <Muted>No active rooms configured</Muted>}
        </DashCard>

        <DashCard title="Needs attention">
          <div style={{ display: 'grid', gap: 10 }}>
            {alerts.map(a => (
              <div key={a.label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13 }}>
                <span style={{ color: 'var(--textSecond)' }}>{a.icon} {a.label}</span>
                <span style={{ fontWeight: 800, minWidth: 28, textAlign: 'center', padding: '2px 8px', borderRadius: 99,
                  background: a.value > 0 ? a.color + '22' : 'var(--bgHover)',
                  color: a.value > 0 ? a.color : 'var(--textFaint)' }}>{a.value}</span>
              </div>
            ))}
          </div>
        </DashCard>
      </div>
    </div>
  );
}

function Muted({ children }) {
  return <div style={{ color: 'var(--textFaint)', fontSize: 12, padding: '30px 0', textAlign: 'center' }}>{children}</div>;
}

function EmptyTrend({ onSave, saving }) {
  return (
    <div style={{ height: 170, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, color: 'var(--textFaint)', fontSize: 12, textAlign: 'center' }}>
      No daily snapshots yet — trends build up as snapshots are saved each day.
      <button style={btnSm} onClick={onSave} disabled={saving}>{saving ? 'Saving…' : "Save today's snapshot"}</button>
    </div>
  );
}
